import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, QrCode, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import api, { getApiErrorMessage } from '../../services/api';

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString('pt-BR');
};

const WhatsAppPage = () => {
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const [statusResponse, qrResponse] = await Promise.all([
        api.get('/whatsapp/status'),
        api.get('/whatsapp/qrcode'),
      ]);

      setStatus(statusResponse.data);
      setQrCode(qrResponse.data.qrCode || null);
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Nao foi possivel consultar o WhatsApp.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  const handleReconnect = async () => {
    setReconnecting(true);
    setError('');

    try {
      await api.post('/whatsapp/reconnect');
      await loadStatus();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Nao foi possivel reconectar o WhatsApp.'));
    } finally {
      setReconnecting(false);
    }
  };

  const connected = Boolean(status?.connected);

  return (
    <div className="flex min-h-full flex-col gap-6 px-1 sm:px-2 lg:px-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
            <div className="rounded-lg bg-emerald-500 p-2">
              <MessageCircle className="text-white" size={20} />
            </div>
            WhatsApp da Portaria
          </h1>
          <p className="text-sm text-gray-500">Conexao usada para avisar moradores sobre encomendas</p>
        </div>

        <button
          type="button"
          onClick={handleReconnect}
          disabled={reconnecting}
          className="flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw size={18} className={reconnecting ? 'animate-spin' : ''} />
          Reconectar
        </button>
      </div>

      <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-4">
            <div
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
                connected ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {connected ? <Wifi size={18} /> : <WifiOff size={18} />}
              {connected ? 'Conectado' : status?.connecting ? 'Conectando' : 'Desconectado'}
            </div>

            <div className="space-y-1 text-sm text-slate-600">
              <p>Ultima atualizacao: {formatDateTime(status?.lastStatusAt)}</p>
              {status?.lastError && <p className="text-red-600">Erro: {status.lastError}</p>}
              {error && <p className="text-red-600">{error}</p>}
            </div>
          </div>

          <div className="flex w-full justify-center lg:w-auto">
            {loading ? (
              <div className="flex h-80 w-80 items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
                Carregando...
              </div>
            ) : connected ? (
              <div className="flex h-80 w-80 flex-col items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50 text-center text-emerald-700">
                <Wifi size={56} />
                <p className="mt-4 text-lg font-bold">WhatsApp conectado</p>
                <p className="mt-1 text-sm">As proximas encomendas serao notificadas automaticamente.</p>
              </div>
            ) : qrCode ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <img src={qrCode} alt="QR Code do WhatsApp" className="h-72 w-72" />
              </div>
            ) : (
              <div className="flex h-80 w-80 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 text-center text-slate-500">
                <QrCode size={54} />
                <p className="mt-4 text-sm font-semibold">QR Code ainda nao disponivel.</p>
                <p className="mt-1 text-xs">Clique em reconectar ou aguarde alguns segundos.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatsAppPage;
